import React, { useRef, useState } from "react";
import { drupalSettings } from "../../../utils/config";
import { useLoader } from "../../Common/GeneralLoader/GeneralLoaderProvider";
import { maskString } from "../../../utils/utils";
import { sendSMSApi, verifySMSApi } from "../../../api/api";
import { useUser } from "../../Contexts/UserContext";

export const ViewPinFormStep1 = ({ setStep }) => {
  const [hasError, setHasError] = useState(false);
  const [isCodeResent, setIsCodeResent] = useState(false);
  const [resendCounter, setResendCounter] = useState(60);
  const { isLoading, showLoader, hideLoader } = useLoader(); // Destructure isLoading, showLoader, and hideLoader
  const { userInfo } = useUser();

  const formErrors = {
    incomplete: Drupal.t("Please enter the verification number"),
    incorrect: Drupal.t("*Please reenter the security code."),
  };
  // Create references for each input element
  const inputsRef = useRef([]);

  // Handle change event to move focus to the next input
  const handleChange = (e, index) => {
    const { value } = e.target;
    // Only move focus if the value is not empty and within bounds
    if (value && index < inputsRef.current.length - 1) {
      inputsRef.current[index + 1].focus();
    }
  };

  // Handle input to allow only numeric values
  const handleInput = (e) => {
    const { value } = e.target;
    // Remove any non-numeric characters
    e.target.value = value.replace(/\D/, "");
  };

  // Handle key down event to support backspace and delete key navigation
  const handleKeyDown = (e, index) => {
    const { key } = e;

    if (
      (key === "Backspace" || key === "Delete") &&
      !e.target.value &&
      index > 0
    ) {
      inputsRef.current[index - 1].focus();
    }
  };

  // Handle paste event
  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").replace(/\D/g, ""); // Get only numeric data

    // Distribute pasted data across inputs
    pasteData.split("").forEach((char, i) => {
      if (i < inputsRef.current.length) {
        inputsRef.current[i].value = char;
        if (i < inputsRef.current.length - 1) {
          inputsRef.current[i + 1].focus();
        }
      }
    });
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    // Collect all input values
    const codeValues = inputsRef.current.map((input) => input.value).join("");

    // Perform your validation here
    if (codeValues.length !== 6) {
      setHasError(formErrors?.incomplete);
    } else {
      setHasError(false);
      showLoader();

      // Mock API call or action to verify the code
      verifySMSApi({
        uid: drupalSettings?.user?.uid,
        sms_code: codeValues,
      })
        .then((data) => {
          if (!data?.status) {
            setHasError(formErrors?.incorrect);
          } else {
            setStep(0);
          }
          hideLoader();
        })
        .catch((error) => {
          console.log(error);
          hideLoader();
        });
    }
  };

  // Handle "Didn’t get your code?" button click
  const handleResendCode = (e) => {
    e.preventDefault();
    showLoader();

    sendSMSApi({
      uid: drupalSettings?.user?.uid,
    })
      .then(function () {
        setIsCodeResent(true);
        setResendCounter(60);

        // Start countdown for enabling the resend button again
        const intervalId = setInterval(() => {
          setResendCounter((prevCounter) => {
            if (prevCounter === 1) {
              clearInterval(intervalId); // Clear the interval when counter reaches 0
              setIsCodeResent(false); // Enable the resend button
              return 0;
            }
            return prevCounter - 1;
          });

          hideLoader();
        }, 1000);
      })
      .catch(function (error) {
        console.log(error);
        hideLoader();
      });
  };

  return (
    <>
      <div className="popup-header">
        <h4 className="popup-title">
          {Drupal.t("Verify Your Identity to View Your PIN")}
        </h4>
      </div>
      <div className="popup-body-content">
        <div className="popup-description">
          {Drupal.t(
            "We've sent a verification code to the mobile number ending in"
          )}{" "}
          <b>{maskString(userInfo?.current_user?.mobile_number, 4)}</b>{" "}
          {Drupal.t("Please enter it below to view your details.")}{" "}
        </div>
        <form className="view-pin-verify-form">
          {hasError ? (
            <div className="popup-has-error form_group">
              <div className="error_messages">{hasError}</div>
            </div>
          ) : null}
          <div className="tfa-code-numbers">
            <div className="panel_body">
              {Array.from({ length: 6 }, (_, index) => (
                <div className="form_item form_group" key={index}>
                  <label
                    className="input_label"
                    htmlFor={`edit-code-${index + 1}`}
                  >
                    {Drupal.t("code")} {index + 1}
                  </label>
                  <input
                    className="form_control"
                    name={`code_${index + 1}`}
                    size="1"
                    maxLength="1"
                    type="tel"
                    inputmode="numeric"
                    ref={(el) => (inputsRef.current[index] = el)} // Assign ref to each input
                    onChange={(e) => handleChange(e, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onInput={handleInput}
                    onPaste={handlePaste}
                  />
                </div>
              ))}
            </div>
          </div>
          <div className={`get-code-action ${isCodeResent ? "disabled" : ""}`}>
            <button
              className={`get-code-btn ${isCodeResent ? "disabled" : ""}`}
              type="submit"
              value={Drupal.t("Didn’t get your code?")}
              onClick={handleResendCode}
            >
              {Drupal.t("Didn’t get your code?")}
            </button>
            {isCodeResent ? (
              <div className="popup-resend-confirm-text">
                <p>{resendCounter}</p>
                <p>
                  {Drupal.t(
                    "We’ve sent you another code. Please reach out to support@secorepay.com if the problem persists."
                  )}
                </p>
              </div>
            ) : null}
          </div>

          <div className="popup_actions">
            <button
              className="popup-primary-btn"
              type="button"
              onClick={handleSubmit}
            >
              {Drupal.t("Submit")}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};
